import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../shared/auth.service';

@Component({
  standalone: true,
  selector: 'app-password-reset-page',
  imports: [CommonModule, FormsModule],
  template: `
  <div class="page">
    <div class="card">
      <div class="hero">
        <div class="brand">
          <img class="brandImg" src="assets/omnifai-logo.png" alt="OmnifAI logo" />
          <div>
            <div class="title">OmnifAI</div>
          </div>
        </div>
        <div class="tagline">Set a new password to continue securely</div>
      </div>

      <h1>Set / Reset Password</h1>

      <div class="form">
        <label>New Password</label>
        <div class="passwordWrap">
          <input [(ngModel)]="password" (ngModelChange)="onPasswordChange()" [type]="showPassword() ? 'text' : 'password'" placeholder="Enter new password" />
          <button
            type="button"
            class="eyeBtn"
            [attr.aria-label]="showPassword() ? 'Hide password' : 'Show password'"
            (click)="togglePasswordVisibility()"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" fill="none" stroke="currentColor" stroke-width="1.8"/>
              <circle cx="12" cy="12" r="3.2" fill="none" stroke="currentColor" stroke-width="1.8"/>
              <path *ngIf="showPassword()" d="M4 20L20 4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        <div class="fieldErr" *ngIf="passwordError()">{{ passwordError() }}</div>

        <label>Confirm Password</label>
        <div class="passwordWrap">
          <input [(ngModel)]="confirmPassword" (ngModelChange)="onConfirmPasswordChange()" [type]="showConfirmPassword() ? 'text' : 'password'" placeholder="Confirm new password" />
          <button
            type="button"
            class="eyeBtn"
            [attr.aria-label]="showConfirmPassword() ? 'Hide password' : 'Show password'"
            (click)="toggleConfirmPasswordVisibility()"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" fill="none" stroke="currentColor" stroke-width="1.8"/>
              <circle cx="12" cy="12" r="3.2" fill="none" stroke="currentColor" stroke-width="1.8"/>
              <path *ngIf="showConfirmPassword()" d="M4 20L20 4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        <div class="fieldErr" *ngIf="confirmError()">{{ confirmError() }}</div>

        <button class="btn primary" (click)="submit()" [disabled]="loading() || linkInvalid()">
          {{ loading() ? 'Saving...' : 'Save Password' }}
        </button>
        <button class="linkBtn" (click)="goLogin()">Back to sign in</button>
      </div>

      <div class="msg ok" *ngIf="success()">{{ success() }}</div>
      <div class="msg err" *ngIf="error()">{{ error() }}</div>
    </div>
  </div>
  `,
  styles: [`
    .page{
      min-height:100vh;display:grid;place-items:center;padding:20px;
      background:
        radial-gradient(900px 480px at 10% 0%, rgba(0,168,132,.14), transparent 62%),
        radial-gradient(900px 480px at 100% 0%, rgba(6,182,212,.12), transparent 56%),
        var(--bg);
    }
    .card{
      width:min(460px,calc(100vw - 24px));
      border:1px solid var(--border);
      background:rgba(17,27,33,.88);
      border-radius:20px;padding:18px;
      box-shadow:0 26px 60px rgba(2,8,23,.28);
    }
    .hero{
      border:1px solid rgba(45,212,191,.22);
      background:
        radial-gradient(560px 180px at -8% -24%, rgba(45,212,191,.16), transparent 58%),
        radial-gradient(500px 180px at 108% -30%, rgba(59,130,246,.14), transparent 56%),
        rgba(15,23,42,.45);
      border-radius:16px;
      padding:12px;
      margin-bottom:8px;
    }
    .brand{display:flex;gap:12px;align-items:center}
    .brandImg{width:48px;height:48px;object-fit:cover;border-radius:14px;flex:0 0 auto}
    .title{font-weight:900;letter-spacing:.3px}
    .tagline{
      margin-top:10px;
      font-size:12px;
      color:var(--muted);
      line-height:1.35;
    }
    h1{margin:14px 0 10px;font-size:20px}
    .form{display:grid;gap:8px}
    label{font-size:12px;color:var(--muted)}
    input{
      height:40px;border-radius:11px;border:1px solid var(--border);background:var(--input);
      color:var(--text);padding:0 10px;outline:none
    }
    .passwordWrap{position:relative}
    .passwordWrap input{width:100%;padding-right:42px}
    .eyeBtn{
      position:absolute;right:8px;top:50%;transform:translateY(-50%);
      width:28px;height:28px;border:0;background:transparent;color:var(--muted);cursor:pointer;padding:0;
      display:grid;place-items:center;
    }
    .eyeBtn svg{width:18px;height:18px}
    .eyeBtn:hover{color:var(--text)}
    .fieldErr{
      font-size:12px;
      color:rgb(251,113,133);
      margin-top:-2px;
    }
    .btn{
      margin-top:8px;height:38px;border-radius:10px;border:1px solid var(--border);background:transparent;
      color:var(--text);cursor:pointer
    }
    .btn.primary{border-color:rgba(0,168,132,.45);background:rgba(0,168,132,.18)}
    .btn:disabled{opacity:.7;cursor:not-allowed}
    .linkBtn{
      margin-top:4px;justify-self:start;background:none;border:0;color:var(--muted);cursor:pointer;padding:0
    }
    .linkBtn:hover{color:var(--text)}
    .msg{margin-top:12px;font-size:12px;padding:8px 10px;border-radius:10px;border:1px solid transparent}
    .msg.ok{border-color:rgba(22,163,74,.45);background:rgba(22,163,74,.16);color:rgb(134,239,172)}
    .msg.err{border-color:rgba(225,29,72,.45);background:rgba(225,29,72,.16);color:rgb(251,113,133)}
    :host-context(body.light) .card{background:rgba(255,255,255,.95);border-color:rgba(15,23,42,.10)}
    :host-context(body.light) .hero{
      background:
        radial-gradient(560px 180px at -8% -24%, rgba(13,148,136,.14), transparent 58%),
        radial-gradient(500px 180px at 108% -30%, rgba(2,132,199,.14), transparent 56%),
        rgba(248,250,252,.96);
      border-color:rgba(2,132,199,.20);
    }
    :host-context(body.light) .title,:host-context(body.light) h1{color:#0f172a}
    :host-context(body.light) label,:host-context(body.light) .linkBtn,:host-context(body.light) .tagline{color:#475569}
    :host-context(body.light) input{background:#fff;color:#0f172a;border-color:rgba(15,23,42,.12)}
  `]
})
export class PasswordResetPageComponent {
  password = '';
  confirmPassword = '';
  orgId = '';
  token = '';
  loading = signal(false);
  error = signal('');
  success = signal('');
  showPassword = signal(false);
  showConfirmPassword = signal(false);
  passwordError = signal('');
  confirmError = signal('');
  linkInvalid = signal(false);
  tokenExpired = signal(false);

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly authService: AuthService
  ) {
    this.orgId = this.route.snapshot.queryParamMap.get('orgId')?.trim() || '';
    this.token = this.route.snapshot.queryParamMap.get('token')?.trim() || '';

    if (!this.orgId || !this.token) {
      this.linkInvalid.set(true);
      this.error.set('Invalid password link. Please request a new set/reset password link.');
    }
  }

  onPasswordChange(): void {
    this.error.set('');
    this.success.set('');
    this.tokenExpired.set(false);
    this.validateInputs(false);
  }

  onConfirmPasswordChange(): void {
    this.error.set('');
    this.success.set('');
    this.tokenExpired.set(false);
    this.validateInputs(false);
  }

  async submit(): Promise<void> {
    this.error.set('');
    this.success.set('');
    this.tokenExpired.set(false);

    if (this.linkInvalid()) {
      this.error.set('Invalid password link. Please request a new set/reset password link.');
      return;
    }

    const isValid = this.validateInputs(true);
    if (!isValid) {
      this.error.set('Please correct the highlighted password fields.');
      return;
    }

    this.loading.set(true);
    try {
      const result = await this.authService.setPassword(this.orgId, this.token, this.password);
      this.loading.set(false);
      if (result.ok) {
        this.success.set('Password updated successfully. Redirecting to sign in...');
        setTimeout(() => this.router.navigateByUrl('/login'), 900);
        return;
      }

      if (result.tokenExpired) {
        this.tokenExpired.set(true);
        this.linkInvalid.set(true);
      }
      this.error.set(result.message || 'Unable to set password. Please request a new link.');
    } finally {
      this.loading.set(false);
    }
  }

  goLogin(): void {
    this.router.navigateByUrl('/login');
  }

  togglePasswordVisibility(): void {
    this.showPassword.set(!this.showPassword());
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword.set(!this.showConfirmPassword());
  }

  private validateInputs(showConfirmWhenEmpty: boolean): boolean {
    const firstPasswordRuleError = this.getFirstPasswordRuleError();
    this.passwordError.set(firstPasswordRuleError);

    if (firstPasswordRuleError) {
      this.confirmError.set('');
      return false;
    }

    if (!this.confirmPassword) {
      this.confirmError.set(showConfirmWhenEmpty ? 'Passwords match' : '');
      return !showConfirmWhenEmpty;
    }

    if (this.password !== this.confirmPassword) {
      this.confirmError.set('Passwords match');
      return false;
    }

    this.confirmError.set('');
    return true;
  }

  private getFirstPasswordRuleError(): string {
    if (this.password.length < 8) return 'Minimum 8 characters';
    if (!/[A-Z]/.test(this.password)) return 'At least one uppercase letter';
    if (!/[a-z]/.test(this.password)) return 'At least one lowercase letter';
    if (!/[0-9]/.test(this.password)) return 'At least one number';
    if (!/[^A-Za-z0-9]/.test(this.password)) return 'At least one special character';
    return '';
  }
}
